import React, {useState} from 'react'
import {
    Box,
    Button,
    SimpleGrid,
    Link as NavLink,
    Image,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import CloudinaryImage from '../../../cloudinary-media/image'
import Title from './partials/title'
import Description from './partials/description'
import ProductsList from './partials/productsList'
import {Link as RouteLink} from 'react-router-dom'
import usePdTagManager from '../../../../hooks/use-pd-tag-manager'
import Video from '../hero/partials/video'
import {getUrl} from '../../../../utils/cloudinary-utils'

const SHOP_THE_LOOK_CLOUDINARY_ASPECT_RATIO = '500:574'

export const ShopTheLook = ({...rest}) => {
    const {
        // General settings
        backgroundColor = 'white',
        openModalWithTiles,
        openModalWithImage,
        redirectionUrl,
        typeId,
        name,

        // Image settings
        desktopImage,
        mobileImage,
        imageAlt,
        dataLayerLoaded = true,
        gifDisableAutoPlay = false,
        desktopStaticImage,
        mobileStaticImage,

        // Video settings
        desktopVideo,
        desktopVideoThumbnail,
        mobileVideo,
        mobileVideoThumbnail,
        videoAlt,
        videoEnableSound,
        videoDisableAutoPlay,

        // Title settings
        title,
        titleColor,
        titleSizeLargeDesktop,
        titleSizeMobile,
        titleSizeSmallDesktop,
        titleSizeTablet,
        titleTag,
        titleFontFamily,
        titleAlign,

        // CTA settings
        ctaSizeDesktop,
        ctaSizeMobile,
        hideCta = false,
        imageCta,
        buttonFontFamily,
        btnBgColor,
        btnTextColor,

        // Description settings
        description,
        descriptionFontFamily,
        descriptionColor,
        descriptionTag,
        descriptionAlign
    } = rest
    const shouldDisplayVideo = desktopVideo || mobileVideo
    const intl = useIntl()
    const pdTagManager = usePdTagManager()
    const modalDisclosure = useDisclosure()

    const desktopImageStyles = {
        maxHeight: {base: 'auto', '2md': '500px', lg: '650px', xl: '700px'}
    }

    const isWhiteBg = (colorValue) => {
        const color = colorValue?.trim().toLowerCase()
        return color === 'white' || color === '#fff' || color === '#ffffff'
    }
    const getHoverMix = (bg) =>
        isWhiteBg(bg)
            ? 'color-mix(in srgb, white 90%, black 10%)'
            : `color-mix(in srgb, ${bg} 88%, white 12%)`

    const analyticsProps = {
        component_id: rest.variant || rest.typeId,
        component_name: title || rest.name || 'ShopTheLook'
    }

    const ctaButtonText =
        imageCta ||
        intl.formatMessage({
            id: 'shop.this.look',
            defaultMessage: 'Shop This Look'
        })

    const ctaSize = {
        base: ctaSizeMobile,
        md: ctaSizeDesktop
    }

    const hexToRgba = (hex, alpha = 1) => {
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
        hex = hex?.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b)

        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
        return result
            ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
                  result[3],
                  16
              )}, ${alpha})`
            : hex
    }

    let redirectionProps = {}
    if (redirectionUrl) {
        const isExternalDomain = redirectionUrl?.indexOf('.com') !== -1
        redirectionProps = isExternalDomain
            ? {as: NavLink, href: redirectionUrl}
            : {as: RouteLink, to: redirectionUrl}
    }
    const [isPlaying, setIsPlaying] = useState(!gifDisableAutoPlay)
    return (
        <SimpleGrid
            columns={{base: 1, md: 2}}
            background={backgroundColor}
            textAlign="left"
            gridTemplateColumns={{
                base: 'repeat(1, minmax(0, 1fr))',
                '2md': 'max-content minmax(0, 1fr)'
            }}
        >
            <Box
                className="desktop-title-description"
                display={{base: 'block', md: 'none'}}
                margin="20px 15px 15px 15px"
            >
                <Title
                    title={title}
                    titleColor={titleColor}
                    titleSizeLargeDesktop={titleSizeLargeDesktop}
                    titleSizeMobile={titleSizeMobile}
                    titleFontFamily={titleFontFamily}
                    titleSizeSmallDesktop={titleSizeSmallDesktop}
                    titleSizeTablet={titleSizeTablet}
                    titleTag={titleTag}
                />
                <Description
                    description={description}
                    descriptionColor={descriptionColor}
                    descriptionTag={descriptionTag}
                    descriptionFontFamily={descriptionFontFamily}
                />
            </Box>
            <Box
                gridArea="600/655.70"
                alignContent="center"
                className="shop-this-look-image"
                background={backgroundColor}
                position="relative"
                cursor={openModalWithImage || redirectionUrl ? 'pointer' : 'auto'}
                onClick={() => {
                    if (openModalWithImage && !redirectionUrl) {
                        modalDisclosure.onOpen()
                    }
                    if (openModalWithImage || redirectionUrl) {
                        pdTagManager.selectComponent({
                            componentId: typeId,
                            componentName: name,
                            titleName: title,
                            linkUrl: redirectionUrl,
                            imageUrl: desktopImage,
                            subText: description,
                            tileHeading: title
                        })
                    }
                }}
                {...redirectionProps}
                sx={{
                    '.component-video': {
                        width: {base: '100% !important', '2md': '500px !important'}
                    }
                }}
            >
                {!shouldDisplayVideo && (
                    <CloudinaryImage
                        src={!isPlaying && !gifDisableAutoPlay ? desktopStaticImage : desktopImage}
                        aspectRatio="577:700"
                        styles={{
                            display: {base: 'none', md: 'block'},
                            width: '100%',
                            height: 'auto',
                            htmlWidth: 500,
                            htmlHeight: 700,
                            ...desktopImageStyles
                        }}
                        imgSizes={{lg: '50vw', md: '50vw', base: '100vw'}}
                        sizes={[1024, 1280, 1440, 1920]}
                        alt={imageAlt}
                    />
                )}
                {!shouldDisplayVideo && (
                    <CloudinaryImage
                        src={!isPlaying && !gifDisableAutoPlay ? mobileStaticImage : mobileImage}
                        aspectRatio="600:656"
                        styles={{
                            display: {base: 'block', md: 'none'},
                            width: '100%',
                            height: 'auto',
                            htmlWidth: 600,
                            htmlHeight: 656
                        }}
                        imgSizes={{lg: '50vw', md: '50vw', base: '100vw'}}
                        sizes={[375, 390, 414, 767]}
                        alt={imageAlt}
                    />
                )}
                {desktopStaticImage && !gifDisableAutoPlay && (
                    <Box position="absolute" top="20px" right="20px" gap="10px" display="grid">
                        <Button
                            onClick={(e) => {
                                e.preventDefault()
                                setIsPlaying(!isPlaying)
                            }}
                            aria-label="Toggle Play/Pause"
                            className="play-pause-btn"
                            gridArea="1 / 1"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            padding="0px"
                            variant="unstyled"
                            height="auto"
                            minHeight="auto"
                            minWidth="auto"
                            gap="10px"
                            zIndex="1"
                        >
                            <Image
                                width="30px"
                                height="30px"
                                src={
                                    isPlaying
                                        ? getUrl('UIImages/images/video-pause-hover.svg')
                                        : getUrl('UIImages/images/video-play-hover.svg')
                                }
                                alt="Pause/ Play Icon"
                                className="control-icon"
                            />
                        </Button>
                    </Box>
                )}
                {shouldDisplayVideo && (
                    <Video
                        desktopVideo={desktopVideo}
                        mobileVideo={mobileVideo || desktopVideo}
                        desktopVideoThumbnail={desktopVideoThumbnail}
                        mobileVideoThumbnail={mobileVideoThumbnail}
                        videoDisableAutoPlay={videoDisableAutoPlay}
                        videoEnableSound={videoEnableSound}
                        videoAlt={videoAlt}
                        cloudinaryAspectRatio={SHOP_THE_LOOK_CLOUDINARY_ASPECT_RATIO}
                    />
                )}
                {!hideCta && (
                    <Button
                        variant="button"
                        onClick={() => {
                            if (openModalWithImage && !redirectionUrl) {
                                modalDisclosure.onOpen()
                            }
                        }}
                        pointerEvents={openModalWithImage || redirectionUrl ? 'auto' : 'none'}
                        className="shop-this-look-btn"
                        background={hexToRgba(btnBgColor, 0.5)}
                        backgroundColor={hexToRgba(btnBgColor, 0.5)}
                        _hover={{
                            background: hexToRgba(btnBgColor, 1),
                            opacity: 1,
                            textDecoration: 'none'
                        }}
                        fontWeight="400"
                        padding="4px 25px !important"
                        color={btnTextColor}
                        fontFamily={buttonFontFamily}
                        borderRadius="40px"
                        aria-label={ctaButtonText}
                        position="absolute"
                        maxWidth={{base: 'calc(100% - 20px)', md: 'unset'}}
                        minHeight="auto"
                        size={ctaSize}
                        bottom={{
                            base: '40px',
                            lg:
                                typeof document === 'undefined'
                                    ? '40px'
                                    : (document
                                          ?.querySelector('.shop-this-look-image')
                                          ?.getBoundingClientRect()?.height -
                                          (document
                                              ?.querySelector('.shop-this-look-image video')
                                              ?.getBoundingClientRect()?.height ||
                                              document
                                                  ?.querySelector('.shop-this-look-image img')
                                                  ?.getBoundingClientRect()?.height)) /
                                          2 +
                                      40 +
                                      'px'
                        }}
                        left="50%"
                        transform="translateX(-50%)"
                        {...redirectionProps}
                        sx={{
                            _hover: {
                                backgroundColor: getHoverMix(btnBgColor)
                            }
                        }}
                    >
                        {ctaButtonText}
                    </Button>
                )}
            </Box>
            <Box
                className="products-section"
                marginTop="auto"
                marginBottom="auto"
                paddingLeft={{
                    lg: '60px',
                    md: '47px',
                    base: 0
                }}
                paddingTop={{md: '5%', lg: '5%', xl: 'unset'}}
                paddingBottom={{md: '5%', lg: '5%', xl: 'unset'}}
            >
                <Box className="desktop-title-description" display={{base: 'none', md: 'block'}}>
                    <Title
                        title={title}
                        titleColor={titleColor}
                        titleSizeLargeDesktop={titleSizeLargeDesktop}
                        titleSizeMobile={titleSizeMobile}
                        titleSizeSmallDesktop={titleSizeSmallDesktop}
                        titleSizeTablet={titleSizeTablet}
                        titleFontFamily={titleFontFamily}
                        titleTag={titleTag}
                        titleAlign={titleAlign}
                    />
                    <Description
                        description={description}
                        descriptionColor={descriptionColor}
                        descriptionTag={descriptionTag}
                        descriptionFontFamily={descriptionFontFamily}
                        descriptionAlign={descriptionAlign}
                    />
                </Box>
                <ProductsList
                    {...rest}
                    desktopImage={desktopImage}
                    mobileImage={mobileImage}
                    cloudinaryAspectRatio={SHOP_THE_LOOK_CLOUDINARY_ASPECT_RATIO}
                    openModalWithTiles={openModalWithTiles}
                    dataLayerLoaded={dataLayerLoaded}
                    modalDisclosure={modalDisclosure}
                    onTileClick={(e) => {
                        if (openModalWithTiles) {
                            e.preventDefault()
                            e.stopPropagation()
                            modalDisclosure.onOpen()
                        }
                    }}
                    analyticsProps={analyticsProps}
                />
            </Box>
        </SimpleGrid>
    )
}

ShopTheLook.displayName = 'ShopTheLook'

export default ShopTheLook
